import React, { FC, useEffect, useMemo } from "react";
import {
  Field,
  FormRenderProps,
  useForm,
  useFormState,
} from "react-final-form";
import { Grid, Typography, makeStyles } from "@material-ui/core";
import clsx from "clsx";
import BootstrapInput from "../../../../../components/BootstrapInput";
import { useQuery } from "react-query";
import useMajorQueries from "../../../../major/hooks/useMajorQueries";
import { QUERY_KEYS } from "../../../../../constants";

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    width: "100%",
    height: 460,
  },
  contentWithScroll: {
    marginRight: -8,
    padding: "0 8px 0 0",
    minHeight: theme.spacing(8),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
    fontFamily: "Roboto",
    fontSize: 16,
    fontWeight: "normal",
    letterSpacing: "-0.1px",
    color: "#535358",
    overflowY: "auto",
    overflowX: "hidden",
    maxHeight: theme.spacing(58),

    "&::-webkit-scrollbar-track": {
      borderRadius: 4,
      background: "#e4e4e4",
    },
    "&::-webkit-scrollbar": {
      width: "8px",
      height: "14px",
      background: "#e4e4e4",
      borderRadius: 4,
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#acacac",
      border: "2px solid #e4e4e4",
      borderBottomWidth: "2px",
      borderRadius: "20px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: "#a8a8a8",
    },
  },
  similarMajorTitle: {
    fontWeight: "bold",
    fontSize: 16,
    marginTop: 16,
    marginBottom: 8,
  },
  similarMajorContainer: {
    height: 334,
    overflowY: "auto",
  },
  selectMajor: {
    color: "#2F80ED",
    fontSize: 14,
    fontWeight: "bold",
    lineHeight: "normal",
    "&+&": {
      marginTop: 8,
    },
    "&:hover": {
      color: "#276ac7",
      cursor: "pointer",
    },
  },
}));

const AddMajor: FC<FormRenderProps> = ({ handleSubmit }) => {
  const classes = useStyles();
  const { majors: majorsQueryKey } = QUERY_KEYS;
  const { values } = useFormState();
  const { fetchAll: fetchAllMajors } = useMajorQueries();
  const { change } = useForm();
  const {
    data: { data: majors },
  } = useQuery<any>(majorsQueryKey, fetchAllMajors);

  const filteredMajors = useMemo(() => {
    const majorName = values.name;

    if (!majorName || majorName.length < 2) {
      return [];
    }

    const enteredWords = majorName
      .trim()
      .replace(/\s+/g, " ")
      .split(" ")
      .map((w) => w.toLowerCase())
      .filter((w) => w.length >= 3);

    return majors
      .map((existingMajor) => {
        const existingWords = existingMajor.name
          .split(" ")
          .map((w) => w.toLowerCase())
          .filter((w) => w !== "the" && w.length >= 3);

        const matchedWordsCount = enteredWords.reduce((count, word) => {
          return existingWords.some((ew) => ew.includes(word))
            ? count + 1
            : count;
        }, 0);

        return {
          major: existingMajor,
          matchedWordsCount,
        };
      })
      .filter((m) => m.matchedWordsCount > 0)
      .sort((a, b) => a.matchedWordsCount - b.matchedWordsCount)
      .map((majorWithCount) => majorWithCount.major);
  }, [majors, values.name]);

  const selectMajor = (major: { id: string; name: string }) => {
    change("id", major.id);
    change("name", major.name);
  };

  useEffect(() => {
    if (values.id) {
      handleSubmit(values as any);
    }
  }, [values, handleSubmit]);

  return (
    <form noValidate onSubmit={handleSubmit}>
      <div className={classes.mainContainer}>
        <Grid item xs={12}>
          <Field
            fullWidth
            type="text"
            label="Major Name"
            placeholder="Enter major name"
            component={BootstrapInput}
            parse={(value) => (value === "" || value === null ? "" : value)}
            name={"name"}
          />
        </Grid>
        {filteredMajors.length > 0 && (
          <>
            <Typography className={classes.similarMajorTitle}>
              You can choose a similar major from the existing ones
            </Typography>
            <Grid
              item
              xs={12}
              className={clsx(
                classes.similarMajorContainer,
                classes.contentWithScroll,
              )}
            >
              {filteredMajors.map((major) => (
                <div
                  key={major.id}
                  className={classes.selectMajor}
                  onClick={() => selectMajor(major)}
                >
                  {major.name}
                </div>
              ))}
            </Grid>
          </>
        )}
      </div>
    </form>
  );
};

export default AddMajor;
